import { BUSINESS_TYPE } from '@/api/types';
import Card, { CardDeadEndHint } from '@/components/Card';
import { ConditionalRender } from '@/components/ui/ConditionalRender';
import { Hint } from '@/components/ui/Hint';
import { useAdditionalInfoContext } from '@/features/v2/lib/additionalInfo';
import { CheckboxGroupDamageRightsAssignments } from '@/features/v2/vehicle-damage/components/CheckboxGroupDamageRightsAssignments';
import {
  CheckboxGroupRepairDocumentsProvided,
  REPAIR_DOCUMENTS_PROVIDED_TO_IC,
  REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/CheckboxGroupRepairDocumentsProvided';
import { InputBeforeAccidentRepairedComplementary } from '@/features/v2/vehicle-damage/components/InputBeforeAccidentRepairedComplementary';
import {
  DAMAGE_BEFORE_ACCIDENT_NAME_COMPLEMENTARY,
  InputDamageBeforeAccidentComplementary,
} from '@/features/v2/vehicle-damage/components/InputDamageBeforeAccidentComplementary';
import { BEFORE_ACCIDENT_REPAIRED_NAME_COMPLEMENTARY } from '@/features/v2/vehicle-damage/components/MultiSelectBeforeAccidentRepaired';
import {
  ORIGINAL_PARTS_USED_FOR_REPAIR_NAME_COMPLEMENTARY,
  SelectOriginalPartsUsedForRepair,
} from '@/features/v2/vehicle-damage/components/OriginalPartsUsedForRepair';
import {
  BUSINESS_ACTIVITY,
  BUSINESS_ACTIVITY_NAME_COMPLEMENTARY,
  RadioGroupBusinessActivity,
} from '@/features/v2/vehicle-damage/components/RadioGroupBusinessActivity';
import {
  RadioGroupNoRepairableDamage,
  UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT,
  UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupNoRepairableDamage';
import {
  RadioGroupPreAccidentRepairs,
  REPAIRS_BEFORE_ACCIDENT,
  REPAIRS_BEFORE_ACCIDENT_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupPreAccidentRepairs';
import {
  RadioGroupVATTaxpayerStatus,
  VAT_PAYER_STATUS,
  VAT_PAYER_STATUS_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupVATTaxpayerStatus';
import {
  RadioGroupVehicleAsFixedAsset,
  VEHICLE_AS_FIXED_ASSET_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleAsFixedAsset';
import {
  RadioGroupVehicleOwner,
  STILL_VEHICLE_OWNER_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleOwner';
import {
  RadioGroupVehicleUsedForBusiness,
  VEHICLE_USED_FOR_BUSINESS,
  VEHICLE_USED_FOR_BUSINESS_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleUsedForBusiness';
import {
  RadioVATDeductionPercentage,
  VAT_DEDUCTION_TYPE_NAME_COMPLEMENTARY,
} from '@/features/v2/vehicle-damage/components/RadioVATDeductionPercentage';
import {
  BUSINESS_TYPE_NAME_COMPLEMENTARY,
  SelectBusinessType,
} from '@/features/v2/vehicle-damage/components/SelectBusinessType';
import { VehicleDamageSchemaDeadEndsStepOneComplementary } from '@/features/v2/vehicle-damage/summarySchema';
import { EnumLike } from '@/types';

import { useLingui } from '@lingui/react';

export const ComplementarySurveyThree = () => {
  const { additionalInfo } = useAdditionalInfoContext();

  const { i18n } = useLingui();

  return (
    <>
      <>
        {!additionalInfo.isVehicleSold && (
          <Card>
            <RadioGroupVehicleOwner isComplementary />
          </Card>
        )}
        <ConditionalRender
          when={(val) =>
            val[STILL_VEHICLE_OWNER_NAME_COMPLEMENTARY] ||
            additionalInfo.isVehicleSold
          }
        >
          <Card>
            <RadioGroupNoRepairableDamage isComplementary />
            <ConditionalRender
              when={(val) =>
                val[UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT_NAME_COMPLEMENTARY] ===
                UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT.YES
              }
            >
              <InputDamageBeforeAccidentComplementary />
            </ConditionalRender>
          </Card>
        </ConditionalRender>
      </>
      <>
        <ConditionalRender
          when={(val) =>
            val[UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT_NAME_COMPLEMENTARY] ===
              UNREPAIRABLE_DAMAGES_BEFORE_ACCIDENT.NO ||
            !!val[DAMAGE_BEFORE_ACCIDENT_NAME_COMPLEMENTARY]?.length
          }
        >
          <Card>
            <RadioGroupPreAccidentRepairs isComplementary />
            <ConditionalRender
              when={(val) =>
                val[REPAIRS_BEFORE_ACCIDENT_NAME_COMPLEMENTARY] ===
                REPAIRS_BEFORE_ACCIDENT.YES
              }
            >
              <SelectOriginalPartsUsedForRepair isComplementary />
              <ConditionalRender
                when={(val) =>
                  val[ORIGINAL_PARTS_USED_FOR_REPAIR_NAME_COMPLEMENTARY]
                }
              >
                <InputBeforeAccidentRepairedComplementary />
              </ConditionalRender>
            </ConditionalRender>
          </Card>
        </ConditionalRender>
        <ConditionalRender
          when={(val) =>
            val[REPAIRS_BEFORE_ACCIDENT_NAME_COMPLEMENTARY] ===
              REPAIRS_BEFORE_ACCIDENT.NO ||
            (val[ORIGINAL_PARTS_USED_FOR_REPAIR_NAME_COMPLEMENTARY] &&
              val[BEFORE_ACCIDENT_REPAIRED_NAME_COMPLEMENTARY]?.length)
          }
        >
          <Card
            isDeadEnd={
              VehicleDamageSchemaDeadEndsStepOneComplementary.civilPartnership
                .condition
            }
          >
            <RadioGroupBusinessActivity isComplementary />
            <ConditionalRender
              when={(val) =>
                val[BUSINESS_ACTIVITY_NAME_COMPLEMENTARY] ===
                BUSINESS_ACTIVITY.YES
              }
            >
              <RadioGroupVehicleUsedForBusiness isComplementary />
              <ConditionalRender
                when={(val) =>
                  val[VEHICLE_USED_FOR_BUSINESS_NAME_COMPLEMENTARY] ===
                  VEHICLE_USED_FOR_BUSINESS.YES
                }
              >
                <SelectBusinessType isComplementary />
                <CardDeadEndHint variant="primary">
                  <Hint.Title>
                    {i18n.t('Twoja sytuacja jest dla nas szczególnie istotna')}
                  </Hint.Title>
                  <Hint.Description className="caption-large lg:text-xs">
                    {i18n.t(
                      'Chcielibyśmy zadać Ci kilka pytań, zanim przejdziemy dalej. W tym celu skontaktujemy się z Tobą wkrótce telefonicznie, aby omówić szczegóły. Do usłyszenia!'
                    )}
                  </Hint.Description>
                </CardDeadEndHint>
                <ConditionalRender
                  when={(val) =>
                    val[BUSINESS_TYPE_NAME_COMPLEMENTARY] &&
                    val[BUSINESS_TYPE_NAME_COMPLEMENTARY] !== BUSINESS_TYPE.SC
                  }
                >
                  <RadioGroupVehicleAsFixedAsset isComplementary />
                  <ConditionalRender
                    when={(val) =>
                      val[VEHICLE_AS_FIXED_ASSET_NAME_COMPLEMENTARY]
                    }
                  >
                    <RadioGroupVATTaxpayerStatus isComplementary />
                    <ConditionalRender
                      when={(val) =>
                        val[VAT_PAYER_STATUS_NAME_COMPLEMENTARY] ===
                        VAT_PAYER_STATUS.YES
                      }
                    >
                      <RadioVATDeductionPercentage isComplementary />
                    </ConditionalRender>
                  </ConditionalRender>
                </ConditionalRender>
              </ConditionalRender>
            </ConditionalRender>
          </Card>
        </ConditionalRender>
        <ConditionalRender
          when={(val) =>
            [
              val[BUSINESS_ACTIVITY_NAME_COMPLEMENTARY],
              val[VEHICLE_USED_FOR_BUSINESS_NAME_COMPLEMENTARY],
              val[VAT_PAYER_STATUS_NAME_COMPLEMENTARY],
            ].some((item) => item === BUSINESS_ACTIVITY.NO) ||
            !!val[VAT_DEDUCTION_TYPE_NAME_COMPLEMENTARY]
          }
        >
          <Card
            isDeadEnd={
              VehicleDamageSchemaDeadEndsStepOneComplementary
                .withAdditionalDocuments.condition
            }
          >
            <CheckboxGroupRepairDocumentsProvided isComplementary />
            <ConditionalRender
              when={(val) =>
                val[REPAIR_DOCUMENTS_PROVIDED_TO_IC_NAME_COMPLEMENTARY]?.some(
                  (item: EnumLike) =>
                    item.value === REPAIR_DOCUMENTS_PROVIDED_TO_IC.ANY
                )
              }
            >
              <CheckboxGroupDamageRightsAssignments isComplementary />
            </ConditionalRender>
            <CardDeadEndHint variant="primary">
              <Hint.Title>
                {i18n.t('Twoja sytuacja jest dla nas szczególnie istotna')}
              </Hint.Title>
              <Hint.Description>
                <p>
                  {i18n.t(
                    'Chcielibyśmy zadać Ci kilka pytań, zanim przejdziemy dalej. W tym celu skontaktujemy się z Tobą wkrótce telefonicznie, aby omówić szczegóły. Do usłyszenia!'
                  )}
                </p>
              </Hint.Description>
            </CardDeadEndHint>
          </Card>
        </ConditionalRender>
      </>
    </>
  );
};
